import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
} from 'react';
import QRCode from 'qrcode';
import {
  generateCalibrationSweep,
  synthesiseOfdmTransmission,
} from '@spectrabus/audio-modem';
import {
  decodeSenderDiscoveryOffer,
  SESSION_DISCOVERY_OFFER_BYTES,
  type MetricSource,
  type SenderDiscoveryOffer,
} from '@spectrabus/protocol';
import { createReferenceObject, type DemoObjectInput } from '@spectrabus/simulation';
import { provenanceLabel, type LinkSnapshot, type Metric } from '@spectrabus/telemetry';
import { createFallbackAudioPlan } from './audio-profile';
import { encodeBootstrapLink, extractBootstrapText } from './bootstrap-link';
import type { RuntimeCommand, RuntimeEvent } from './demo-runtime';
import {
  useLiveReceiver,
  useLiveSender,
  type LiveReceiverController,
} from './live-session';

export interface SpectraBusAppProps {
  readonly mode: 'demo' | 'sender' | 'receiver';
}

type RuntimeState = 'idle' | 'preparing' | 'running' | 'failed';

// iOS mutes Web Audio output while the ring/silent switch is engaged unless
// the page declares a playback audio session, so the acoustic probe would
// emit silence on a muted iPhone without this declaration.
function requestPlaybackAudioSession(): void {
  const audioSession = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
  if (audioSession !== undefined) audioSession.type = 'playback';
}

// A phone left displaying an optical raster or holding a live camera aperture
// must not dim and lock mid-transfer; the screen is the physical channel.
// Absence of the Wake Lock API, or platform refusal (for example low-power
// mode), simply leaves the display on its normal timeout.
function useScreenWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return undefined;
    let sentinel: WakeLockSentinel | null = null;
    let disposed = false;
    const acquire = async (): Promise<void> => {
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (disposed) {
          void lock.release();
          return;
        }
        sentinel = lock;
      } catch {
        sentinel = null;
      }
    };
    // The platform releases the lock whenever the tab is hidden, so the lock
    // must be reacquired every time the page becomes visible again.
    const reacquire = (): void => {
      if (!disposed && document.visibilityState === 'visible') void acquire();
    };
    void acquire();
    document.addEventListener('visibilitychange', reacquire);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', reacquire);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}

const MODE_COPY = {
  demo: {
    eyebrow: 'SIGNATURE DEMONSTRATION / DETERMINISTIC CHANNEL',
    title: ['One object.', 'Two physical media.', 'No network path.'],
    body: 'A reproducible 12 MB transfer exercises calibration, parallel transport, obstruction, interference, directed repair and absolute verification.',
    provenance: 'SIMULATED PHYSICAL CHANNEL · REAL CIPHERTEXT · REAL FOUNTAIN RANK · REAL FINAL HASH',
  },
  sender: {
    eyebrow: 'OBJECT INGRESS / SENDER',
    title: ['Put the object', 'on the air.'],
    body: 'Selected bytes are sealed locally and bound to an ephemeral session, then emitted for real over the display and speaker while the microphone listens on the acoustic return band for the receiver\'s physical response.',
    provenance: 'LOCAL OBJECT PROCESSING · LIVE PHYSICAL EMISSION · NO UPLOAD',
  },
  receiver: {
    eyebrow: 'PHYSICAL ACQUISITION / RECEIVER',
    title: ['Acquire', 'the physical bus.'],
    body: 'Open the camera and microphone and aim at the sender\'s display: the receiver derives session keys, answers over the acoustic return band, admits authenticated frames and reconstructs the object physically.',
    provenance: 'LIVE PHY ACQUISITION · LIVE RECONSTRUCTION · NO AUTOMATIC OPEN',
  },
} as const;

const PHASES = [
  'DISCOVER',
  'CALIBRATE',
  'KEY_CONFIRM',
  'TRANSFER',
  'REPAIR',
  'VERIFY',
  'COMPLETE',
] as const;

const MAX_BROWSER_OBJECT_BYTES = 64 * 1024 * 1024;

export const BOOTSTRAP_QR_OPTIONS = Object.freeze({
  errorCorrectionLevel: 'Q' as const,
  margin: 4,
  width: 320,
  color: { dark: '#071011ff', light: '#f4f8f7ff' },
});

export interface CompletionPresentation {
  readonly title: string;
  readonly statement: string;
  readonly releaseState: string;
  readonly footer: string;
  readonly canSave: boolean;
}

export function completionPresentation(
  mode: SpectraBusAppProps['mode'],
  downloadAvailable: boolean,
  channelSource: MetricSource = 'simulated',
): CompletionPresentation {
  if (channelSource !== 'measured') {
    if (mode === 'demo') {
      return {
        title: 'OBJECT VERIFIED',
        statement: 'The deterministic receiver resolved the same 256-bit object commitment through simulated physical impairments.',
        releaseState: downloadAvailable ? 'CONTROL OBJECT AVAILABLE' : 'WITHHELD',
        footer: 'Cryptography and reconstruction are real; no physical receiver acknowledgement is claimed.',
        canSave: downloadAvailable,
      };
    }
    return {
      title: 'CONTROL OBJECT VERIFIED',
      statement: 'The deterministic receiver reconstructed and verified the selected bytes; this run modelled the physical channel.',
      releaseState: !downloadAvailable
        ? 'WITHHELD'
        : mode === 'receiver'
          ? 'CONTROL OBJECT AVAILABLE'
          : 'CONTROL RUN CLOSED',
      footer: mode === 'receiver'
        ? 'Live camera and microphone acquisition have not yet completed physical reconstruction.'
        : 'No physical receiver acknowledgement is claimed by this browser milestone.',
      canSave: mode === 'receiver' && downloadAvailable,
    };
  }
  if (mode === 'sender') {
    return {
      title: 'RECEIVER VERIFIED',
      statement: 'The authenticated return channel closed the session on the same 256-bit object commitment.',
      releaseState: 'ACKNOWLEDGED',
      footer: 'The source object remains local. SpectraBus creates no automatic duplicate.',
      canSave: false,
    };
  }
  return {
    title: 'OBJECT VERIFIED',
    statement: mode === 'receiver'
      ? 'The reconstructed bytes resolved to the sender\'s 256-bit object commitment.'
      : 'The deterministic receiver resolved the same 256-bit object commitment as the sender.',
    releaseState: downloadAvailable ? 'AVAILABLE' : 'WITHHELD',
    footer: mode === 'receiver'
      ? 'Content is never opened or executed automatically.'
      : 'The verified control object is released only by explicit save.',
    canSave: downloadAvailable,
  };
}

export interface FailurePresentation {
  readonly code: string;
  readonly title: string;
  readonly detail: string;
  readonly guidance: string;
  readonly action: string;
}

export function failurePresentation(
  message: string | null,
  mode: SpectraBusAppProps['mode'],
): FailurePresentation {
  const detail = message?.trim() || 'The transfer runtime stopped without a diagnostic.';
  const normalised = detail.toLowerCase();
  const action = mode === 'sender'
    ? 'RETURN TO OBJECT INGRESS'
    : mode === 'receiver'
      ? 'RETURN TO PHYSICAL RECEIVER'
      : 'RETURN TO DEMONSTRATION';
  if (/integrity|hash|merkle|aes|gcm|authentication|binding|crc|protocol/u.test(normalised)) {
    return {
      code: 'INTEGRITY REJECTED',
      title: 'Verification rejected the transfer.',
      detail,
      guidance: 'No object was released. Establish a fresh session and reacquire both physical channels.',
      action,
    };
  }
  if (/worker|runtime/u.test(normalised)) {
    return {
      code: 'RUNTIME STOP',
      title: 'The transfer runtime stopped.',
      detail,
      guidance: 'No object was released. Return, keep this tab in the foreground and begin a new session.',
      action,
    };
  }
  return {
    code: 'TRANSFER STOPPED',
    title: 'The transfer could not continue.',
    detail,
    guidance: 'No object was released. Return and begin a new physical session.',
    action,
  };
}

export function captureFailureMessage(error: unknown): string {
  const named = typeof error === 'object' && error !== null
    ? error as { readonly name?: unknown; readonly message?: unknown }
    : null;
  const name = typeof named?.name === 'string' ? named.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Camera or microphone access was denied. Allow both permissions in site settings, then open the receiver again.';
  }
  if (name === 'NotFoundError') {
    return 'A camera and microphone are both required, but at least one device is unavailable.';
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return 'The camera or microphone could not be started. Close any other application using the hardware, then retry.';
  }
  const message = typeof named?.message === 'string' ? named.message.trim() : '';
  return message === ''
    ? 'Hardware capture stopped before the physical receiver could be established.'
    : `Hardware capture stopped: ${message}`;
}

export interface CarrierPlanSummary {
  readonly active: number;
  readonly total: number;
  readonly minimumFrequencyHz: number;
  readonly maximumFrequencyHz: number;
  readonly modulationCounts: Readonly<Record<'OFF' | 'PILOT' | 'BPSK' | 'QPSK' | '16QAM', number>>;
}

export function summariseCarrierPlan(carriers: LinkSnapshot['carriers']): CarrierPlanSummary {
  const modulationCounts = { OFF: 0, PILOT: 0, BPSK: 0, QPSK: 0, '16QAM': 0 };
  for (const carrier of carriers) modulationCounts[carrier.modulation] += 1;
  return {
    active: carriers.filter((carrier) => carrier.enabled).length,
    total: carriers.length,
    minimumFrequencyHz: carriers[0]?.frequencyHz ?? 0,
    maximumFrequencyHz: carriers.at(-1)?.frequencyHz ?? 0,
    modulationCounts,
  };
}

export function parseSpectraBusBootstrap(value: string): SenderDiscoveryOffer {
  const bootstrapText = extractBootstrapText(value);
  if (bootstrapText === null) {
    throw new Error('Bootstrap payload does not carry the SB1 prefix.');
  }
  const encoded = bootstrapText.slice(4);
  if (encoded === '' || !/^[A-Za-z\d_-]+$/u.test(encoded)) {
    throw new Error('Bootstrap payload is not canonical base64url.');
  }
  const paddingLength = (4 - (encoded.length % 4)) % 4;
  if (paddingLength === 3) {
    throw new Error('Bootstrap payload has an invalid base64url length.');
  }
  let binary: string;
  try {
    binary = atob(`${encoded.replace(/-/gu, '+').replace(/_/gu, '/')}${'='.repeat(paddingLength)}`);
  } catch {
    throw new Error('Bootstrap payload could not be decoded as base64url.');
  }
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (bytes.length !== SESSION_DISCOVERY_OFFER_BYTES) {
    throw new Error(`Bootstrap sender offer must contain exactly ${SESSION_DISCOVERY_OFFER_BYTES} bytes.`);
  }
  return decodeSenderDiscoveryOffer(bytes);
}

function formatBytes(value: number): string {
  if (value < 1_000) return `${value.toLocaleString('en-GB')} B`;
  if (value < 1_000_000) return `${(value / 1_000).toFixed(1)} kB`;
  return `${(value / 1_000_000).toFixed(2)} MB`;
}

function formatRate(bitsPerSecond: number): string {
  if (bitsPerSecond < 1_000) return `${bitsPerSecond.toFixed(0)} bit/s`;
  if (bitsPerSecond < 1_000_000) return `${(bitsPerSecond / 1_000).toFixed(1)} kbit/s`;
  return `${(bitsPerSecond / 1_000_000).toFixed(2)} Mbit/s`;
}

function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

function formatSessionId(value: string): string {
  if (value.length < 16) return value.toUpperCase();
  return `${value.slice(0, 4)}-${value.slice(4, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}…`.toUpperCase();
}

function MetricSource({ metric }: { readonly metric: Metric<unknown> }): React.JSX.Element {
  return <span className="source-tag">{provenanceLabel(metric.source)}</span>;
}

function OpticalField({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const context = canvas.getContext('2d', { alpha: false });
    if (context === null) return;
    const cellWidth = canvas.width / snapshot.currentFrameWidth;
    const cellHeight = canvas.height / snapshot.currentFrameHeight;
    const palette = ['#0f1516', '#e6f1ef', '#41d4c3', '#e6ab50'] as const;
    context.fillStyle = '#0b1011';
    context.fillRect(0, 0, canvas.width, canvas.height);
    for (let y = 0; y < snapshot.currentFrameHeight; y += 1) {
      for (let x = 0; x < snapshot.currentFrameWidth; x += 1) {
        const value = snapshot.currentFrameCells[y * snapshot.currentFrameWidth + x] ?? 0;
        context.fillStyle = palette[value] ?? '#0f1516';
        context.fillRect(
          Math.floor(x * cellWidth),
          Math.floor(y * cellHeight),
          Math.ceil(cellWidth),
          Math.ceil(cellHeight),
        );
      }
    }
  }, [snapshot]);

  return (
    <section className="transport-field" aria-label="Live optical transport field">
      <div className="field-heading">
        <span>LIVE OPTICAL TRANSPORT FIELD</span>
        <span>{provenanceLabel(snapshot.provenance.raster)} · {snapshot.opticalProfile}</span>
      </div>
      <div className="aperture-shell">
        <canvas
          ref={canvasRef}
          className="optical-canvas"
          width={960}
          height={640}
          role="img"
          aria-label={`Current ${snapshot.currentFrameWidth} by ${snapshot.currentFrameHeight} encoded optical raster with timing and calibration pilots`}
        >
          Encoded optical frame: {snapshot.currentFrameWidth} by {snapshot.currentFrameHeight} cells, {snapshot.opticalProfile}.
        </canvas>
        <div className="aperture-index aperture-index--top">
          {snapshot.currentFrameWidth} × {snapshot.currentFrameHeight} CELLS
        </div>
        <div className="aperture-index aperture-index--bottom">
          {snapshot.handshake.transferAuthorised
            ? 'O1 CRC32C · SBFA SESSION BOUND · FOUNTAIN GF(2)'
            : 'O1 CRC32C · CANONICAL SBHS OFFER · NO PAYLOAD CREDIT'}
        </div>
      </div>
      <div className="spatial-quality-strip" aria-hidden="true">
        {snapshot.spatialRegions.map((region) => (
          <span
            key={region.index}
            className={region.enabled ? 'spatial-region' : 'spatial-region spatial-region--reduced'}
            style={{ '--quality': region.quality } as CSSProperties}
          />
        ))}
      </div>
    </section>
  );
}

function CarrierMap({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  const summary = summariseCarrierPlan(snapshot.carriers);
  return (
    <section className="carrier-panel" aria-label="Acoustic carrier allocation">
      <div className="field-heading">
        <span>ACOUSTIC CARRIER FIELD</span>
        <span>{provenanceLabel(snapshot.provenance.carrierPlan)} · {snapshot.acousticProfile}</span>
      </div>
      <div className="carrier-map" aria-hidden="true">
        {snapshot.carriers.map((carrier) => {
          const height = Math.max(4, Math.min(100, ((carrier.snrDb + 4) / 38) * 100));
          return (
            <span
              key={carrier.index}
              className="carrier"
              data-modulation={carrier.modulation}
              title={`${carrier.frequencyHz.toFixed(0)} Hz · ${carrier.snrDb.toFixed(1)} dB · ${carrier.modulation}`}
              style={{ '--carrier-height': `${height}%` } as CSSProperties}
            />
          );
        })}
      </div>
      <div className="carrier-axis">
        <span>{(summary.minimumFrequencyHz / 1_000).toFixed(2)} kHz</span>
        <span>{summary.active} ACTIVE</span>
        <span>{(summary.maximumFrequencyHz / 1_000).toFixed(2)} kHz</span>
      </div>
      <details className="carrier-summary">
        <summary>
          <span>INSPECT EXACT CARRIER PLAN</span>
          <strong>{summary.active} / {summary.total} ACTIVE</strong>
        </summary>
        <div className="carrier-summary-body">
          <dl>
            <div><dt>Pilot</dt><dd>{summary.modulationCounts.PILOT}</dd></div>
            <div><dt>BPSK</dt><dd>{summary.modulationCounts.BPSK}</dd></div>
            <div><dt>QPSK</dt><dd>{summary.modulationCounts.QPSK}</dd></div>
            <div><dt>16QAM</dt><dd>{summary.modulationCounts['16QAM']}</dd></div>
            <div><dt>Notched</dt><dd>{summary.modulationCounts.OFF}</dd></div>
          </dl>
          <div className="carrier-summary-scroll" tabIndex={0}>
            <table>
              <caption className="visually-hidden">Exact acoustic carrier allocation</caption>
              <thead>
                <tr><th scope="col">Carrier</th><th scope="col">Frequency</th><th scope="col">SNR</th><th scope="col">Mode</th><th scope="col">State</th></tr>
              </thead>
              <tbody>
                {snapshot.carriers.map((carrier) => (
                  <tr key={carrier.index}>
                    <th scope="row">{carrier.index.toString().padStart(2, '0')}</th>
                    <td>{carrier.frequencyHz.toFixed(0)} Hz</td>
                    <td>{carrier.snrDb.toFixed(1)} dB</td>
                    <td>{carrier.modulation}</td>
                    <td>{carrier.enabled ? 'ACTIVE' : 'NOTCHED'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </details>
    </section>
  );
}

function SchedulerSeam({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const context = canvas.getContext('2d', { alpha: false });
    if (context === null) return;
    const opticalIndependent = snapshot.counters.optical.independent;
    const acousticIndependent = snapshot.counters.acoustic.independent;
    const slots = 96;
    const slotWidth = canvas.width / slots;
    const allocationSplit = Math.round(slots * snapshot.opticalAllocation);
    const admitted = opticalIndependent + acousticIndependent;
    const admissionSplit = admitted === 0
      ? 0
      : Math.round(slots * (opticalIndependent / admitted));
    context.fillStyle = '#0a0f10';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#273135';
    context.fillRect(0, Math.floor(canvas.height / 2), canvas.width, 1);
    for (let index = 0; index < slots; index += 1) {
      context.globalAlpha = 0.82;
      context.fillStyle = index < allocationSplit ? '#4adbc8' : '#edb15a';
      context.fillRect(
        Math.floor(index * slotWidth),
        5,
        Math.max(1, Math.ceil(slotWidth * 0.58)),
        Math.floor(canvas.height / 2) - 10,
      );
      if (admitted > 0) {
        context.globalAlpha = 0.96;
        context.fillStyle = index < admissionSplit ? '#4adbc8' : '#edb15a';
        context.fillRect(
          Math.floor(index * slotWidth),
          Math.floor(canvas.height / 2) + 6,
          Math.max(1, Math.ceil(slotWidth * 0.58)),
          Math.floor(canvas.height / 2) - 11,
        );
      }
    }
    context.globalAlpha = 1;
  }, [snapshot.acousticAllocation, snapshot.counters.acoustic, snapshot.counters.optical, snapshot.opticalAllocation]);

  return (
    <section className="scheduler-seam" aria-label="Derived real-time channel allocation">
      <div className="field-heading">
        <span>ADAPTIVE SCHEDULER / ALLOCATION + CUMULATIVE ADMISSION MIX</span>
        <span>{provenanceLabel(snapshot.provenance.scheduler)} · {formatPercent(snapshot.opticalAllocation, 0)} LIGHT / {formatPercent(snapshot.acousticAllocation, 0)} SOUND</span>
      </div>
      <canvas
        ref={canvasRef}
        width={1152}
        height={54}
        role="img"
        aria-label={`${snapshot.counters.optical.independent} optical and ${snapshot.counters.acoustic.independent} acoustic independent symbols admitted`}
      >
        Scheduler allocation: {formatPercent(snapshot.opticalAllocation, 0)} optical and {formatPercent(snapshot.acousticAllocation, 0)} acoustic.
      </canvas>
    </section>
  );
}

function RankField({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const context = canvas.getContext('2d', { alpha: false });
    if (context === null) return;
    context.fillStyle = '#0b1011';
    context.fillRect(0, 0, canvas.width, canvas.height);
    const count = Math.max(1, snapshot.generations.length);
    const width = canvas.width / count;
    for (let index = 0; index < snapshot.generations.length; index += 1) {
      const generation = snapshot.generations[index];
      if (generation === undefined) continue;
      const fraction = Math.max(0.025, generation.rank / generation.requiredRank);
      const height = Math.max(1, Math.round(fraction * (canvas.height - 4)));
      context.globalAlpha = generation.verified ? 0.96 : 0.72;
      context.fillStyle = generation.verified ? '#edf4f2' : '#41d4c3';
      context.fillRect(
        Math.floor(index * width),
        canvas.height - height,
        Math.max(1, Math.ceil(width * 0.72)),
        height,
      );
    }
    context.globalAlpha = 1;
    context.fillStyle = '#3c484b';
    context.fillRect(0, canvas.height - 1, canvas.width, 1);
  }, [snapshot.generations]);

  return (
    <section className="rank-section" aria-label="Independent information recovered">
      <div className="rank-heading">
        <div>
          <span className="eyebrow">INDEPENDENT INFORMATION RECOVERED / {provenanceLabel(snapshot.provenance.admissions)}</span>
          <strong>{formatPercent(snapshot.reconstructedFraction)}</strong>
        </div>
        <div className="rank-count">
          <span>{snapshot.totalIndependentSymbols.toLocaleString('en-GB')}</span>
          <small>/ {snapshot.requiredIndependentSymbols.toLocaleString('en-GB')} DOF</small>
        </div>
      </div>
      <div
        className="rank-map"
        role="progressbar"
        aria-label="Independent information recovered"
        aria-valuemin={0}
        aria-valuemax={snapshot.requiredIndependentSymbols}
        aria-valuenow={snapshot.totalIndependentSymbols}
        aria-valuetext={`${formatPercent(snapshot.reconstructedFraction)} reconstructed; ${snapshot.unresolvedSymbols} unresolved symbols`}
      >
        <canvas
          ref={canvasRef}
          width={1536}
          height={40}
          aria-hidden="true"
        >
          {snapshot.totalIndependentSymbols} of {snapshot.requiredIndependentSymbols} independent degrees of freedom recovered.
        </canvas>
      </div>
      <div className="rank-footer">
        <span>{formatBytes(snapshot.verifiedBytes)} cryptographically admitted</span>
        <span>{snapshot.unresolvedSymbols.toLocaleString('en-GB')} unresolved symbols</span>
        <span className={snapshot.integrityViolations === 0 ? 'verified-text' : 'failure-text'}>
          {snapshot.integrityViolations} integrity violations
        </span>
      </div>
    </section>
  );
}

function PhaseRail({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  const currentIndex = PHASES.indexOf(snapshot.phase as (typeof PHASES)[number]);
  return (
    <ol className="phase-rail" aria-label="Transfer state">
      {PHASES.map((phase, index) => {
        const state = index < currentIndex ? 'complete' : index === currentIndex ? 'active' : 'pending';
        return (
          <li
            className="phase-step"
            data-state={state}
            key={phase}
            aria-current={state === 'active' ? 'step' : undefined}
            aria-label={`${phase.replace('_', ' ')}, ${state}`}
          >
            <span>{String(index + 1).padStart(2, '0')}</span>
            <strong>{phase.replace('_', ' ')}</strong>
          </li>
        );
      })}
    </ol>
  );
}

function ChannelTelemetry({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  return (
    <aside className="channel-telemetry" aria-label="Channel telemetry">
      <div className="telemetry-intro">
        <span className="eyebrow">COMMIT-VERIFIED GOODPUT</span>
        <MetricSource metric={snapshot.aggregateGoodput} />
        <strong>{formatRate(snapshot.aggregateGoodput.value)}</strong>
        <small>new ciphertext committed by SHA-256 / elapsed transfer time</small>
      </div>
      <div className="channel-row channel-row--optical">
        <div>
          <span>OPTICAL PHY</span>
          <MetricSource metric={snapshot.opticalGoodput} />
        </div>
        <strong>{formatRate(snapshot.opticalGoodput.value)}</strong>
        <small>{formatPercent(snapshot.opticalAllocation, 0)} allocation · {formatRate(snapshot.opticalRawBitrate.value)} raw</small>
      </div>
      <div className="allocation-track">
        <span style={{ width: formatPercent(snapshot.opticalAllocation, 2) }} />
      </div>
      <div className="channel-row channel-row--acoustic">
        <div>
          <span>ACOUSTIC PHY</span>
          <MetricSource metric={snapshot.acousticGoodput} />
        </div>
        <strong>{formatRate(snapshot.acousticGoodput.value)}</strong>
        <small>{formatPercent(snapshot.acousticAllocation, 0)} allocation · {formatRate(snapshot.acousticRawBitrate.value)} raw</small>
      </div>
      <div className="allocation-track allocation-track--amber">
        <span style={{ width: formatPercent(snapshot.acousticAllocation, 2) }} />
      </div>
      <dl className="telemetry-list">
        <div>
          <dt>Spatial use</dt>
          <dd>{formatPercent(snapshot.spatialUtilisation.value, 0)}</dd>
        </div>
        <div>
          <dt>Display cadence</dt>
          <dd>{snapshot.displayCadence.value.toFixed(0)} frame/s</dd>
        </div>
        <div>
          <dt>Feedback RTT</dt>
          <dd>{snapshot.feedbackRtt.value.toFixed(0)} ms</dd>
        </div>
        <div>
          <dt>Link stability</dt>
          <dd>{formatPercent(snapshot.environmentalStability.value, 0)}</dd>
        </div>
      </dl>
      <div className="incident-stack">
        {snapshot.incidents.map((incident) => (
          <div
            key={incident.id}
            className={incident.active ? 'incident incident--active' : 'incident'}
            data-resolved={incident.resolved}
          >
            <span>{incident.active ? 'ACTIVE' : incident.resolved ? 'RECOVERED' : 'ARMED'}</span>
            <strong>{incident.label}</strong>
          </div>
        ))}
      </div>
    </aside>
  );
}

function IntegrityChain({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  return (
    <section className="integrity-chain" aria-label="Integrity verification chain">
      <div className="field-heading">
        <span>INTEGRITY CHAIN</span>
        <span>{snapshot.phase === 'COMPLETE' ? 'CLOSED' : 'OPEN'}</span>
      </div>
      <ol>
        {snapshot.integrityChecks.map((check) => (
          <li
            key={check.stage}
            data-state={check.state}
            title={check.detail}
            aria-label={`${check.label}: ${check.state}. ${check.detail}`}
          >
            <span />
            <strong>{check.label}</strong>
            <small>{check.state.toUpperCase()}</small>
          </li>
        ))}
      </ol>
    </section>
  );
}

type BootstrapRasterState =
  | { readonly status: 'rendering' }
  | { readonly status: 'ready'; readonly source: string }
  | { readonly status: 'failed'; readonly detail: string };

function BootstrapPanel({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  const [raster, setRaster] = useState<BootstrapRasterState>({ status: 'rendering' });
  // The QR carries a receiver link with the offer in the fragment, so a
  // native camera scan routes straight to the receiver application already
  // holding the offer; in-page scanners accept both the link and raw form.
  const bootstrap = useMemo(
    () => encodeBootstrapLink(`SB1:${snapshot.handshake.encodedOffer}`),
    [snapshot.handshake.encodedOffer],
  );

  useEffect(() => {
    let active = true;
    setRaster({ status: 'rendering' });
    void QRCode.toDataURL(bootstrap, BOOTSTRAP_QR_OPTIONS)
      .then((value) => {
        if (active) setRaster({ status: 'ready', source: value });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const detail = error instanceof Error && error.message.trim() !== ''
          ? `QR encoder stopped: ${error.message}`
          : 'The QR encoder stopped without a diagnostic.';
        setRaster({ status: 'failed', detail });
      });
    return () => {
      active = false;
    };
  }, [bootstrap]);

  return (
    <section className="bootstrap-panel" aria-labelledby="bootstrap-title">
      <div>
        <span className="eyebrow">ROBUST BOOTSTRAP</span>
        <h2 id="bootstrap-title">VISUAL DISCOVERY PREAMBLE</h2>
        <p>Canonical sender offer: protocol, transfer binding, object commitment, capabilities and ephemeral key.</p>
      </div>
      {raster.status === 'rendering' ? (
        <div className="qr-placeholder" role="status" aria-live="polite">
          <span>CONSTRUCTING BOOTSTRAP</span>
        </div>
      ) : raster.status === 'ready' ? (
        <img src={raster.source} alt="SpectraBus session bootstrap QR code, dark modules on a light field" />
      ) : (
        <div className="qr-failure" role="alert">
          <strong>BOOTSTRAP UNAVAILABLE</strong>
          <p>{raster.detail}</p>
          <small>Return to ingress and create a fresh session. Bulk transfer has not started.</small>
        </div>
      )}
    </section>
  );
}

function CompleteReveal({
  snapshot,
  mode,
}: {
  readonly snapshot: LinkSnapshot;
  readonly mode: SpectraBusAppProps['mode'];
}): React.JSX.Element {
  const presentation = completionPresentation(
    mode,
    snapshot.downloadBytes !== null,
    snapshot.provenance.channel,
  );
  const download = useCallback(() => {
    if (snapshot.downloadBytes === null) return;
    const ownedBytes = new Uint8Array(snapshot.downloadBytes.length);
    ownedBytes.set(snapshot.downloadBytes);
    const blob = new Blob([ownedBytes.buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = snapshot.objectName;
    anchor.hidden = true;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }, [snapshot.downloadBytes, snapshot.objectName]);

  return (
    <section className="complete-reveal" aria-labelledby="completion-title">
      <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {presentation.title}. {snapshot.provenance.channel === 'measured'
          ? 'Measured physical channel.'
          : 'Simulated physical channel control run.'} {presentation.statement} {snapshot.objectBytes.toLocaleString('en-GB')} bytes. SHA-256 identical. Zero unresolved symbols. Zero integrity violations.
      </p>
      <h2 id="completion-title">{presentation.title}</h2>
      <strong>{snapshot.objectBytes.toLocaleString('en-GB')} bytes</strong>
      <p>{presentation.statement}</p>
      <div className="hash-equality" aria-label="Sender and receiver SHA-256 values are identical">
        <div>
          <span>SENDER / SHA-256</span>
          <code>{snapshot.senderHash}</code>
        </div>
        <i><span>256 / 256 BITS EQUAL</span></i>
        <div>
          <span>RECEIVER / SHA-256</span>
          <code>{snapshot.objectHash}</code>
        </div>
      </div>
      <dl>
        <div><dt>Unresolved symbols</dt><dd>{snapshot.unresolvedSymbols}</dd></div>
        <div><dt>Integrity violations</dt><dd>{snapshot.integrityViolations}</dd></div>
        <div><dt>Release state</dt><dd>{presentation.releaseState}</dd></div>
      </dl>
      {presentation.canSave ? (
        <button type="button" className="primary-action" onClick={download}>
          SAVE VERIFIED OBJECT
        </button>
      ) : null}
      <small>{presentation.footer}</small>
    </section>
  );
}

function EngineeringView({ snapshot }: { readonly snapshot: LinkSnapshot }): React.JSX.Element {
  const opticalAttempts = snapshot.counters.optical.accepted + snapshot.counters.optical.rejected;
  const acousticAttempts = snapshot.counters.acoustic.accepted + snapshot.counters.acoustic.rejected;
  const opticalDecode = opticalAttempts === 0 ? 0 : snapshot.counters.optical.accepted / opticalAttempts;
  const acousticDecode = acousticAttempts === 0 ? 0 : snapshot.counters.acoustic.accepted / acousticAttempts;
  const missing = snapshot.generations.filter((generation) => !generation.verified);

  return (
    <section className="engineering-view" aria-labelledby="engineering-title">
      <h2 id="engineering-title" className="visually-hidden">Engineering diagnostics</h2>
      <section className="engineering-handshake" aria-labelledby="handshake-title">
        <div>
          <h3 id="handshake-title">SESSION HANDSHAKE</h3>
          <span className="source-tag">{provenanceLabel(snapshot.handshake.source)}</span>
        </div>
        <dl>
          <div><dt>Protocol</dt><dd>{snapshot.handshake.protocol}</dd></div>
          <div><dt>Key agreement</dt><dd>{snapshot.handshake.keyAgreement}</dd></div>
          <div><dt>Offer ↔ response</dt><dd data-state={snapshot.handshake.offerResponseBound ? 'verified' : 'pending'}>{snapshot.handshake.offerResponseBound ? 'BOUND' : 'PENDING'}</dd></div>
          <div><dt>Confirmations</dt><dd>{snapshot.handshake.verifiedConfirmations} / {snapshot.handshake.requiredConfirmations} VERIFIED <span>{provenanceLabel(snapshot.handshake.transportSource)} TRANSPORT</span></dd></div>
          <div><dt>Confirmed media</dt><dd>{snapshot.handshake.mutuallyConfirmedChannels.length === 0 ? 'NONE' : snapshot.handshake.mutuallyConfirmedChannels.map((channel) => channel.toUpperCase()).join(' + ')} <span>{provenanceLabel(snapshot.handshake.transportSource)}</span></dd></div>
          <div><dt>Transfer authorised</dt><dd data-state={snapshot.handshake.transferAuthorised ? 'verified' : 'pending'}>{snapshot.handshake.transferAuthorised ? 'YES' : 'NO'}</dd></div>
          <div><dt>Transcript SHA-256</dt><dd><code title={snapshot.handshake.transcriptHash} aria-label={`Transcript SHA-256 ${snapshot.handshake.transcriptHash}`}>{snapshot.handshake.transcriptHash.slice(0, 16)}…</code></dd></div>
        </dl>
      </section>
      <div className="engineering-grid">
        <div>
          <h3>OPTICAL DECODER</h3>
          <dl className="diagnostic-list">
            <div><dt>Decode success</dt><dd>{formatPercent(opticalDecode)} <span>{provenanceLabel(snapshot.provenance.admissions)}</span></dd></div>
            <div><dt>Camera timing</dt><dd>{snapshot.cameraFps.value?.toFixed(0) ?? '—'} fps <span>{provenanceLabel(snapshot.cameraFps.source)}</span></dd></div>
            <div><dt>Decoder latency</dt><dd>{snapshot.decoderLatency.value.toFixed(0)} ms <span>{provenanceLabel(snapshot.decoderLatency.source)}</span></dd></div>
            <div><dt>Selected profile</dt><dd>{snapshot.opticalProfile}</dd></div>
            <div><dt>Dropped camera frames</dt><dd>UNAVAILABLE</dd></div>
            <div><dt>Rolling shutter</dt><dd>UNAVAILABLE IN O1</dd></div>
          </dl>
          <div className="region-diagnostics">
            {snapshot.spatialRegions.map((region) => (
              <span key={region.index} data-enabled={region.enabled}>
                <strong>{region.index.toString().padStart(2, '0')}</strong>
                <small>{formatPercent(region.quality, 0)}</small>
              </span>
            ))}
          </div>
        </div>
        <div>
          <h3>ACOUSTIC DECODER</h3>
          <dl className="diagnostic-list">
            <div><dt>Packet success</dt><dd>{formatPercent(acousticDecode)} <span>{provenanceLabel(snapshot.provenance.admissions)}</span></dd></div>
            <div><dt>Carrier plan</dt><dd>{snapshot.acousticProfile}</dd></div>
            <div><dt>Disabled carriers</dt><dd>{snapshot.carriers.filter((carrier) => !carrier.enabled).length}</dd></div>
            <div><dt>Feedback round trip</dt><dd>{snapshot.feedbackRtt.value.toFixed(0)} ms <span>{provenanceLabel(snapshot.feedbackRtt.source)}</span></dd></div>
            <div><dt>AGC behaviour</dt><dd>NOT MEASURED</dd></div>
            <div><dt>Raw bitrate</dt><dd>{formatRate(snapshot.acousticRawBitrate.value)} <span>{provenanceLabel(snapshot.acousticRawBitrate.source)}</span></dd></div>
          </dl>
          <div className="carrier-table">
            {snapshot.carriers.map((carrier) => (
              <span key={carrier.index} data-enabled={carrier.enabled} title={`${carrier.frequencyHz.toFixed(0)} Hz`}>
                {carrier.modulation}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h3>FOUNTAIN / SCHEDULER</h3>
          <dl className="diagnostic-list">
            <div><dt>Fountain rank</dt><dd>{snapshot.totalIndependentSymbols} / {snapshot.requiredIndependentSymbols}</dd></div>
            <div><dt>Missing generations</dt><dd>{missing.length}</dd></div>
            <div><dt>FEC overhead</dt><dd>{formatPercent(snapshot.fountainOverhead.value)}</dd></div>
            <div><dt>Duplicate rate</dt><dd>{formatPercent(snapshot.duplicateRate.value)}</dd></div>
            <div><dt>Optical accepted</dt><dd>{snapshot.counters.optical.accepted}</dd></div>
            <div><dt>Acoustic accepted</dt><dd>{snapshot.counters.acoustic.accepted}</dd></div>
            <div><dt>Optical raw bitrate</dt><dd>{formatRate(snapshot.opticalRawBitrate.value)}</dd></div>
          </dl>
        </div>
        <div>
          <h3>RUNTIME / INTEGRITY</h3>
          <dl className="diagnostic-list">
            <div><dt>Channel provenance</dt><dd>{provenanceLabel(snapshot.provenance.channel)}</dd></div>
            <div><dt>Crypto provenance</dt><dd>{provenanceLabel(snapshot.provenance.cryptography)} / EXECUTED</dd></div>
            <div><dt>Payload cryptography</dt><dd>EXECUTED / REAL</dd></div>
            <div><dt>Key fingerprint</dt><dd title={snapshot.sessionFingerprint} aria-label={`Key fingerprint ${snapshot.sessionFingerprint}`}>{snapshot.sessionFingerprint.slice(0, 12)}…</dd></div>
            <div><dt>Thermal throttling</dt><dd>UNAVAILABLE</dd></div>
            <div><dt>Transfer runtime</dt><dd>DEDICATED WORKER</dd></div>
            <div><dt>Object exposure</dt><dd>{snapshot.downloadBytes === null ? 'WITHHELD' : 'VERIFIED'}</dd></div>
            <div><dt>Integrity violations</dt><dd>{snapshot.integrityViolations}</dd></div>
          </dl>
        </div>
      </div>
    </section>
  );
}

interface AcousticAcquisition {
  readonly state: 'inactive' | 'listening' | 'locked' | 'rejected' | 'failed';
  readonly detail: string;
  readonly cfo: string;
}

interface OpticalAcquisition {
  readonly state: 'inactive' | 'searching' | 'locked' | 'failed';
  readonly detail: string;
  readonly cadence: string;
}

interface BootstrapAcquisition {
  readonly state: 'inactive' | 'unavailable' | 'searching' | 'validated' | 'rejected';
  readonly detail: string;
  readonly session: string;
  readonly transfer: string;
}

export function captureProvenance(
  captureState: 'idle' | 'opening' | 'active' | 'failed',
  bootstrapState: BootstrapAcquisition['state'],
  opticalState: OpticalAcquisition['state'],
  acousticState: AcousticAcquisition['state'],
): string {
  if (captureState !== 'active') return 'INACTIVE';
  const admitted: string[] = [];
  if (bootstrapState === 'validated') admitted.push('BOOTSTRAP VALIDATED');
  if (opticalState === 'locked') admitted.push('O1 DECODED');
  if (acousticState === 'locked') admitted.push('A1 DECODED');
  return `${admitted.length > 0 ? admitted.join(' + ') : 'LIVE CAPTURE'} / SETTINGS REPORTED`;
}

/**
 * Mounts an imperatively owned video element into the layout. The element is
 * created by the live capture controller and must survive React unmounting
 * whichever container currently shows it, so it is reparented rather than
 * recreated: a torn-down element would drop the camera pipeline mid-session.
 */
function VideoMount({
  element,
  className,
}: {
  readonly element: HTMLVideoElement | null;
  readonly className: string;
}): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (container === null || element === null) return undefined;
    container.appendChild(element);
    // Chromium pauses a media element while it is detached from the
    // document, so a reparented live viewfinder would freeze on its last
    // frame and the video-frame callback chain would stall with it. The
    // camera pipeline only survives the move if playback resumes here.
    if (element.paused) void element.play().catch(() => undefined);
    return () => {
      if (element.parentElement === container) container.removeChild(element);
    };
  }, [element]);
  return <div ref={containerRef} className={className} />;
}

/**
 * The optical return emitter: the current return payload rendered as a QR
 * for the sender's camera. Docked so it stays visible while the console
 * scrolls — this display is a physical transmitter, not a status widget.
 */
function ReturnOpticalDock({ text }: { readonly text: string }): React.JSX.Element | null {
  const [source, setSource] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    void QRCode.toDataURL(text, BOOTSTRAP_QR_OPTIONS)
      .then((value) => {
        if (active) setSource(value);
      })
      .catch(() => {
        if (active) setSource(null);
      });
    return () => {
      active = false;
    };
  }, [text]);
  if (source === null) return null;
  return (
    <aside className="live-return-dock" aria-label="Optical return transmitter">
      <img src={source} alt="Optical return payload QR code for the sender camera" />
      <span>OPTICAL RETURN / SHOW TO SENDER CAMERA</span>
    </aside>
  );
}

function LiveReceiverPanel({
  receiver,
  onReference,
}: {
  readonly receiver: LiveReceiverController;
  readonly onReference: () => void;
}): React.JSX.Element {
  const opening = receiver.state === 'opening';
  const active = receiver.state === 'scanning' || receiver.state === 'session';
  const heading = receiver.state === 'session'
    ? 'PHYSICAL SESSION LIVE'
    : receiver.state === 'scanning'
      ? 'PHYSICAL APERTURE OPEN'
      : receiver.state === 'failed'
        ? 'PHYSICAL INPUT UNAVAILABLE'
        : 'REAL PHY INPUT';
  return (
    <section className="receiver-probe" data-state={receiver.state} aria-labelledby="receiver-heading">
      <div className="receiver-copy">
        <div>
          <span className="eyebrow">LIVE CAMERA + MICROPHONE</span>
          <h2 id="receiver-heading">{heading}</h2>
          <p role={receiver.state === 'failed' ? 'alert' : 'status'} aria-atomic="true">
            {receiver.failureMessage === null
              ? receiver.statusDetail
              : captureFailureMessage(new Error(receiver.failureMessage))}
          </p>
        </div>
        <dl className="capture-readout">
          <div>
            <dt>Camera cadence</dt>
            <dd>{receiver.cameraFps === null ? '—' : `${receiver.cameraFps} fps MEASURED`}</dd>
          </div>
          <div>
            <dt>Forward channels</dt>
            <dd>
              {receiver.speakerPriority
                ? 'O1 RASTER ONLY / SPEAKER PRIORITY'
                : 'O1 RASTER + A1 OFDM 2.1–8.3 kHz'}
            </dd>
          </div>
          <div>
            <dt>Return channel</dt>
            <dd>A1 OFDM 9.6–15.2 kHz / SPEAKER</dd>
          </div>
          <div>
            <dt>Bootstrap</dt>
            <dd>{receiver.state === 'session' ? 'OFFER ACCEPTED · KEYS DERIVED' : 'QR OR O1 DISCOVERY OFFER'}</dd>
          </div>
          <div>
            <dt>Provenance</dt>
            <dd>{active ? 'MEASURED / THIS APERTURE' : 'INACTIVE'}</dd>
          </div>
        </dl>
        <div className="probe-actions">
          {!active ? (
            <button
              type="button"
              className="primary-action"
              onClick={() => void receiver.open()}
              disabled={opening}
            >
              {opening ? 'OPENING HARDWARE…' : 'OPEN PHYSICAL RECEIVER'}
            </button>
          ) : (
            <button type="button" onClick={receiver.reset}>RELEASE CAPTURE</button>
          )}
          {active ? (
            <button type="button" onClick={() => void receiver.switchCamera()}>
              SWITCH CAMERA ({receiver.cameraFacing === 'user' ? 'FRONT' : 'REAR'})
            </button>
          ) : (
            <button type="button" onClick={onReference} disabled={opening}>
              RUN CONTROL SCENARIO
            </button>
          )}
        </div>
        <small>
          LIVE PATH — DECODED PAYLOAD ENTERS FRAME AUTHENTICATION, FOUNTAIN ADMISSION AND
          SEALED-OBJECT VERIFICATION.
        </small>
      </div>
    </section>
  );
}

const A1_PROOF_PAYLOAD = new TextEncoder().encode('SpectraBus/A1 physical acquisition proof');

function AudibleProbe(): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef(0);
  const contextRef = useRef<AudioContext | null>(null);
  const [state, setState] = useState<'idle' | 'sweep' | 'burst' | 'failed'>('idle');
  const [emissionFailure, setEmissionFailure] = useState<string | null>(null);
  const [emissions, setEmissions] = useState({ sweeps: 0, bursts: 0 });

  const draw = useCallback((samples: Float32Array, progress: number) => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const context = canvas.getContext('2d', { alpha: false });
    if (context === null) return;
    context.fillStyle = '#0b1011';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#273135';
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(0, canvas.height / 2);
    context.lineTo(canvas.width, canvas.height / 2);
    context.stroke();
    context.strokeStyle = '#e6ab50';
    context.beginPath();
    const bucket = Math.max(1, Math.floor(samples.length / canvas.width));
    for (let x = 0; x < canvas.width; x += 1) {
      let peak = 0;
      const start = x * bucket;
      for (let index = start; index < Math.min(samples.length, start + bucket); index += 1) {
        peak = Math.max(peak, Math.abs(samples[index] ?? 0));
      }
      const height = peak * canvas.height * 0.43;
      context.moveTo(x + 0.5, canvas.height / 2 - height);
      context.lineTo(x + 0.5, canvas.height / 2 + height);
    }
    context.stroke();
    if (progress > 0) {
      context.fillStyle = '#edf4f2';
      context.fillRect(Math.min(canvas.width - 1, Math.round(progress * canvas.width)), 0, 1, canvas.height);
    }
  }, []);

  useEffect(() => {
    const sweep = generateCalibrationSweep(48_000);
    draw(sweep, 0);
    return () => {
      cancelAnimationFrame(animationRef.current);
      void contextRef.current?.close();
      contextRef.current = null;
    };
  }, [draw]);

  const play = useCallback(async (emission: 'sweep' | 'burst') => {
    setState(emission);
    setEmissionFailure(null);
    try {
      requestPlaybackAudioSession();
      const context = new AudioContext({ sampleRate: 48_000, latencyHint: 'interactive' });
      contextRef.current = context;
      await context.resume();
      const samples = emission === 'sweep'
        ? generateCalibrationSweep(context.sampleRate)
        : synthesiseOfdmTransmission(
            A1_PROOF_PAYLOAD,
            createFallbackAudioPlan(context.sampleRate),
          ).samples;
      const buffer = context.createBuffer(1, samples.length, context.sampleRate);
      const channelData = new Float32Array(samples.length);
      channelData.set(samples);
      buffer.copyToChannel(channelData, 0);
      const source = context.createBufferSource();
      const gain = context.createGain();
      gain.gain.value = 0.75;
      source.buffer = buffer;
      source.connect(gain);
      gain.connect(context.destination);
      const startTime = context.currentTime + 0.04;
      const duration = samples.length / context.sampleRate;
      const animate = (): void => {
        const progress = Math.max(0, Math.min(1, (context.currentTime - startTime) / duration));
        draw(samples, progress);
        if (progress < 1) animationRef.current = requestAnimationFrame(animate);
      };
      source.onended = () => {
        cancelAnimationFrame(animationRef.current);
        draw(samples, 1);
        source.disconnect();
        gain.disconnect();
        void context.close();
        contextRef.current = null;
        // An emission counts only once its playback actually completed.
        setEmissions((current) => emission === 'sweep'
          ? { ...current, sweeps: current.sweeps + 1 }
          : { ...current, bursts: current.bursts + 1 });
        setState('idle');
      };
      source.start(startTime);
      animationRef.current = requestAnimationFrame(animate);
    } catch (error) {
      console.error('SpectraBus acoustic calibration emission failed.', error);
      cancelAnimationFrame(animationRef.current);
      void contextRef.current?.close();
      contextRef.current = null;
      setState('failed');
      setEmissionFailure(error instanceof Error && error.message.trim() !== ''
        ? `Audio output stopped: ${error.message}. Check the selected output device and retry.`
        : 'Audio output stopped before emission. Check the selected output device and retry.');
    }
  }, [draw]);

  return (
    <section className="audible-probe" data-state={state} aria-labelledby="audible-heading">
      <div className="probe-waveform">
        <canvas
          ref={canvasRef}
          width={480}
          height={116}
          role="img"
          aria-label={state === 'burst'
            ? 'Generated A1 OFDM modem burst amplitude over time'
            : 'Generated audible calibration sweep amplitude over time'}
        >
          {state === 'burst'
            ? 'Generated A1 OFDM modem burst waveform.'
            : 'Generated calibration sweep waveform from 1.5 to 15.5 kHz.'}
        </canvas>
      </div>
      <span>AUDIBLE CHANNEL PROBE / REAL WAVEFORM</span>
      <h2 id="audible-heading">{state === 'sweep'
        ? 'EMITTING 1.5–15.5 kHz'
        : state === 'burst'
          ? 'EMITTING A1 / OFDM'
          : state === 'failed'
            ? 'EMISSION FAILED'
            : 'Sound the shared space.'}</h2>
      <p role={state === 'failed' ? 'alert' : undefined}>
        {emissionFailure ?? 'Emit the calibrated sweep, then prove the streaming receiver with a framed, CRC-checked A1 burst. No ultrasonic claim.'}
      </p>
      <div className="probe-emissions">
        <button type="button" onClick={() => void play('sweep')} disabled={state === 'sweep' || state === 'burst'}>
          {state === 'sweep' ? 'EMITTING SWEEP…' : 'CALIBRATION SWEEP'}
        </button>
        <button type="button" onClick={() => void play('burst')} disabled={state === 'sweep' || state === 'burst'}>
          {state === 'burst' ? 'EMITTING BURST…' : 'A1 MODEM BURST'}
        </button>
      </div>
      <small>{state === 'sweep' || state === 'burst' ? 'EMITTED / REAL' : 'EXPERIMENTAL HARDWARE OUTPUT'}</small>
      <small className="emission-ledger" role="status">
        EMITTED {emissions.bursts} A1 BURST{emissions.bursts === 1 ? '' : 'S'} ({A1_PROOF_PAYLOAD.length} B PAYLOAD EACH)
        {' + '}{emissions.sweeps} SWEEP{emissions.sweeps === 1 ? '' : 'S'}
        {' · '}NO RETURN CHANNEL — RECEPTION IS MEASURED ON THE RECEIVER
      </small>
    </section>
  );
}

function PhysicalContract(): React.JSX.Element {
  return (
    <ol className="physical-contract-list" aria-label="SpectraBus transfer contract">
      <li><span>01</span><div><strong>SEAL</strong><small>Authenticated object generations</small></div></li>
      <li><span>02</span><div><strong>SEND</strong><small>One stream over light + sound</small></div></li>
      <li><span>03</span><div><strong>ADMIT</strong><small>Independent information only</small></div></li>
      <li><span>04</span><div><strong>VERIFY</strong><small>Hash equality or no object</small></div></li>
    </ol>
  );
}

function IdleGate({
  mode,
  onObject,
  receiver,
}: {
  readonly mode: SpectraBusAppProps['mode'];
  readonly onObject: (input: DemoObjectInput) => Promise<void>;
  readonly receiver?: LiveReceiverController;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [ingressFailure, setIngressFailure] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const copy = MODE_COPY[mode];

  const sendText = useCallback(async () => {
    setIngressFailure(null);
    const bytes = new TextEncoder().encode(messageText);
    if (bytes.length === 0) {
      setIngressFailure('Type at least one character to transfer a text message.');
      return;
    }
    try {
      await onObject({ bytes, name: 'message.txt', mimeType: 'text/plain' });
    } catch (error) {
      setIngressFailure(
        error instanceof Error ? error.message : 'The text message could not be prepared.',
      );
    }
  }, [messageText, onObject]);

  const readFile = useCallback(
    async (file: File) => {
      setIngressFailure(null);
      if (file.size === 0) {
        setIngressFailure('The selected object is empty. At least one byte is required.');
        return;
      }
      if (file.size > MAX_BROWSER_OBJECT_BYTES) {
        setIngressFailure(`The browser vertical slice accepts objects up to ${formatBytes(MAX_BROWSER_OBJECT_BYTES)}.`);
        return;
      }
      try {
        await onObject({
          bytes: new Uint8Array(await file.arrayBuffer()),
          name: file.name,
          mimeType: file.type || 'application/octet-stream',
        });
      } catch (error) {
        setIngressFailure(error instanceof Error ? error.message : 'The selected object could not be read.');
      }
    },
    [onObject],
  );

  const drop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragging(false);
      const file = event.dataTransfer.files[0];
      if (file !== undefined) void readFile(file);
    },
    [readFile],
  );

  return (
    <main className="idle-gate" data-mode={mode}>
      <div
        className={dragging ? 'drop-plane drop-plane--active' : 'drop-plane'}
        onDragEnter={mode === 'receiver' ? undefined : () => setDragging(true)}
        onDragLeave={mode === 'receiver' ? undefined : () => setDragging(false)}
        onDragOver={mode === 'receiver' ? undefined : (event) => event.preventDefault()}
        onDrop={mode === 'receiver' ? undefined : drop}
      >
        {mode === 'receiver' ? (
          <div className="idle-copy idle-copy--receiver">
            <div className="receiver-copy-col">
              <span className="eyebrow">{copy.eyebrow}</span>
              <h1>{copy.title.map((line, index) => <span key={line} data-line={index + 1}>{line}</span>)}</h1>
              <p>{copy.body}</p>
              <small>{copy.provenance}</small>
            </div>
            <div
              className="drop-mark drop-mark--stage"
              data-live={receiver?.videoElement === null ? undefined : 'true'}
            >
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <VideoMount element={receiver?.videoElement ?? null} className="stage-video" />
              <i className="stage-gate" aria-hidden="true" />
              <em className="stage-status" role="status">
                {receiver?.state === 'session'
                  ? 'SESSION LOCKED'
                  : receiver?.state === 'scanning'
                    ? 'O1 + QR ACQUISITION'
                    : 'OPTICAL SENSOR CLOSED'}
              </em>
            </div>
          </div>
        ) : (
        <div className="idle-copy">
          <span className="eyebrow">{copy.eyebrow}</span>
          <h1>{copy.title.map((line, index) => <span key={line} data-line={index + 1}>{line}</span>)}</h1>
          <div className="drop-mark" aria-hidden="true"><span /><span /><span /><span /></div>
          <p>{copy.body}</p>
          <div className="idle-actions">
            <button type="button" className="primary-action" onClick={() => inputRef.current?.click()}>
              {mode === 'demo' ? 'SELECT REAL OBJECT' : 'SELECT OBJECT'}
            </button>
            <button type="button" onClick={() => void onObject(createReferenceObject(256 * 1024))}>
              RUN 256 KB QUICK REFERENCE
            </button>
            {mode === 'demo' ? (
              <button type="button" onClick={() => void onObject(createReferenceObject())}>
                RUN 12 MB REFERENCE
              </button>
            ) : null}
          </div>
          <div className="text-ingress">
            <label htmlFor="text-ingress-input">TEXT MESSAGE / SEALED AS TEXT-PLAIN</label>
            <textarea
              id="text-ingress-input"
              rows={3}
              maxLength={65_536}
              placeholder="Type a short message and move it through the complete integrity chain."
              value={messageText}
              onChange={(event) => setMessageText(event.currentTarget.value)}
            />
            <button
              type="button"
              onClick={() => void sendText()}
              disabled={messageText.trim() === ''}
            >
              SEAL + TRANSFER TEXT
            </button>
          </div>
          {ingressFailure === null ? null : <p className="ingress-failure" role="alert">{ingressFailure}</p>}
          <small>{copy.provenance}</small>
        </div>
        )}
        <input
          ref={inputRef}
          className="visually-hidden"
          type="file"
          aria-label="Choose an object to transfer"
          tabIndex={-1}
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            if (file !== undefined) void readFile(file);
          }}
        />
        <PhysicalContract />
      </div>
      {mode === 'receiver' && receiver !== undefined ? (
        <LiveReceiverPanel
          receiver={receiver}
          onReference={() => void onObject(createReferenceObject())}
        />
      ) : <AudibleProbe />}
    </main>
  );
}

function PreparingPlane(): React.JSX.Element {
  return (
    <main className="preparing-plane" aria-live="polite">
      <div className="calibration-scan" aria-hidden="true" />
      <span className="eyebrow">PREPARE</span>
      <h1>Sealing object and constructing transport generations.</h1>
      <p>AES-256-GCM · SHA-256 chunk commitments · Merkle root · systematic GF(2)</p>
    </main>
  );
}

type PresentationRate = 1 | 8 | 32;
const PRESENTATION_RATES: readonly PresentationRate[] = [1, 8, 32];

export function SpectraBusApp({ mode }: SpectraBusAppProps): React.JSX.Element {
  const [runtime, setRuntime] = useState<RuntimeState>('idle');
  const [snapshot, setSnapshot] = useState<LinkSnapshot | null>(null);
  const [engineeringOpen, setEngineeringOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const runIdRef = useRef(0);
  const frameRef = useRef(0);
  const previousFrameRef = useRef(0);
  const advanceInFlightRef = useRef(false);
  // The channel is simulated and already labelled as such, so accelerating
  // model time is presentation, not fabrication: throughput figures stay in
  // model time and provenance labels are untouched.
  const [presentationRate, setPresentationRate] = useState<PresentationRate>(1);
  const presentationRateRef = useRef<PresentationRate>(1);
  const liveSender = useLiveSender(mode === 'sender');
  const liveReceiver = useLiveReceiver(mode === 'receiver');
  const liveSnapshot = mode === 'sender'
    ? liveSender.snapshot
    : mode === 'receiver'
      ? liveReceiver.snapshot
      : null;
  const liveConsoleFailed = liveSnapshot !== null && liveSnapshot.phase === 'FAILED';
  const consoleSnapshot = liveConsoleFailed
    ? null
    : liveSnapshot ?? (runtime === 'running' ? snapshot : null);
  useScreenWakeLock(
    (runtime === 'running' && snapshot?.phase !== 'COMPLETE' && snapshot?.phase !== 'FAILED') ||
      liveReceiver.state === 'scanning' ||
      liveReceiver.state === 'session' ||
      (liveSnapshot !== null && liveSnapshot.phase !== 'COMPLETE' && liveSnapshot.phase !== 'FAILED'),
  );
  const failureView = failurePresentation(
    liveConsoleFailed ? liveSnapshot.failure : failure,
    mode,
  );
  const returnLabel = mode === 'receiver'
    ? 'Return to physical receiver'
    : mode === 'sender'
      ? 'Return to object ingress'
      : 'Return to demonstration ingress';

  useEffect(() => {
    let worker: Worker;
    try {
      worker = new Worker(new URL('./demo-runtime.worker.ts', import.meta.url), {
        name: 'spectrabus-transfer-runtime',
        type: 'module',
      });
    } catch (error) {
      setFailure(error instanceof Error && error.message.trim() !== ''
        ? `The transfer worker could not start: ${error.message}`
        : 'The transfer worker could not start in this browser context.');
      setRuntime('failed');
      return undefined;
    }
    workerRef.current = worker;

    const scheduleAdvance = (): void => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame((now) => {
        if (advanceInFlightRef.current) return;
        const previous = previousFrameRef.current || now;
        if (now - previous < 32) {
          scheduleAdvance();
          return;
        }
        previousFrameRef.current = now;
        advanceInFlightRef.current = true;
        const command: RuntimeCommand = {
          type: 'advance',
          runId: runIdRef.current,
          deltaMs: Math.max(1, Math.min(100, now - previous)) * presentationRateRef.current,
        };
        worker.postMessage(command);
      });
    };

    worker.onmessage = (event: MessageEvent<RuntimeEvent>) => {
      const message = event.data;
      if (message.runId !== runIdRef.current) return;
      advanceInFlightRef.current = false;
      if (message.type === 'failed') {
        setFailure(message.message);
        setRuntime('failed');
        return;
      }

      setSnapshot(message.snapshot);
      setRuntime('running');
      if (message.snapshot.phase === 'FAILED') {
        setFailure(message.snapshot.failure);
        setRuntime('failed');
        return;
      }
      if (message.snapshot.phase !== 'COMPLETE') scheduleAdvance();
    };

    worker.onerror = (event) => {
      advanceInFlightRef.current = false;
      setFailure(event.message || 'The transfer worker stopped unexpectedly.');
      setRuntime('failed');
    };

    return () => {
      cancelAnimationFrame(frameRef.current);
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  const begin = useCallback(async (input: DemoObjectInput) => {
    setRuntime('preparing');
    setFailure(null);
    setSnapshot(null);
    const worker = workerRef.current;
    if (worker === null) {
      setFailure('The transfer worker is not ready.');
      setRuntime('failed');
      return;
    }
    cancelAnimationFrame(frameRef.current);
    advanceInFlightRef.current = false;
    previousFrameRef.current = performance.now();
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    const bytes = input.bytes.slice().buffer;
    const command: RuntimeCommand = {
      type: 'prepare',
      runId,
      input: { bytes, name: input.name, mimeType: input.mimeType },
    };
    worker.postMessage(command, [bytes]);
  }, []);

  const liveSenderReset = liveSender.reset;
  const liveReceiverReset = liveReceiver.reset;
  const reset = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    advanceInFlightRef.current = false;
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    workerRef.current?.postMessage({ type: 'reset', runId } satisfies RuntimeCommand);
    liveSenderReset();
    liveReceiverReset();
    setSnapshot(null);
    setFailure(null);
    setEngineeringOpen(false);
    setRuntime('idle');
  }, [liveSenderReset, liveReceiverReset]);

  const liveSenderBegin = liveSender.begin;
  const beginObject = useCallback(
    async (input: DemoObjectInput) => {
      if (mode === 'sender') {
        // The sender app moves real bytes over the physical channels; the
        // deterministic control run remains the demo application's job.
        await liveSenderBegin(input);
        return;
      }
      await begin(input);
    },
    [mode, begin, liveSenderBegin],
  );

  const headerSnapshot = consoleSnapshot ?? liveSnapshot ?? snapshot;
  return (
    <div className="spectrabus-app" data-phase={headerSnapshot?.phase ?? runtime}>
      <header className="top-rail">
        <button type="button" className="wordmark" onClick={reset} aria-label={returnLabel}>
          <span>SPECTRA</span><strong>BUS</strong>
        </button>
        <div className="transport-contract">
          <span>DISPLAY → CAMERA</span>
          <i />
          <span>SPEAKER → MICROPHONE</span>
        </div>
        <div className="session-block">
          <span>{mode.toUpperCase()} / PHYSICAL LINK ONLY</span>
          <strong
            title={headerSnapshot?.sessionId}
            aria-label={headerSnapshot === null ? 'No active session' : `Session ${headerSnapshot.sessionId.toUpperCase()}`}
          >
            {headerSnapshot === null ? 'NO SESSION' : formatSessionId(headerSnapshot.sessionId)}
          </strong>
        </div>
      </header>

      {runtime === 'idle' && liveSnapshot === null ? (
        <IdleGate
          mode={mode}
          onObject={beginObject}
          receiver={mode === 'receiver' ? liveReceiver : undefined}
        />
      ) : null}
      {runtime === 'preparing' ? <PreparingPlane /> : null}
      {runtime === 'failed' || liveConsoleFailed ? (
        <main className="failure-plane" role="alert" aria-labelledby="failure-title">
          <span>{failureView.code} / OBJECT WITHHELD</span>
          <h1 id="failure-title">{failureView.title}</h1>
          <p className="failure-detail">{failureView.detail}</p>
          <p className="failure-guidance">{failureView.guidance}</p>
          <button type="button" onClick={reset}>{failureView.action}</button>
          {mode !== 'demo' ? (
            <button
              type="button"
              onClick={() =>
                void (mode === 'sender'
                  ? liveSender.copyDiagnostics()
                  : liveReceiver.copyDiagnostics())
              }
            >
              COPY DIAGNOSTICS
            </button>
          ) : null}
        </main>
      ) : null}

      {consoleSnapshot !== null && runtime !== 'failed' ? (
        <>
          <main className="transfer-console" aria-labelledby="transfer-title">
            <h1 id="transfer-title" className="visually-hidden">
              SpectraBus {mode} transfer session
            </h1>
            <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
              {consoleSnapshot.phase.replace('_', ' ')}. {consoleSnapshot.phaseDetail}
            </p>
            <PhaseRail snapshot={consoleSnapshot} />
            <div className="physical-plane">
              <div className="state-line">
                <div>
                  <span>{consoleSnapshot.phase}</span>
                  <strong>{consoleSnapshot.phaseDetail}</strong>
                </div>
                <div>
                  <span className="simulation-flag">{provenanceLabel(consoleSnapshot.provenance.channel)} PHY</span>
                  <span>{consoleSnapshot.objectName}</span>
                  <strong>{formatBytes(consoleSnapshot.objectBytes)}</strong>
                </div>
              </div>
              {consoleSnapshot.phase === 'DISCOVER' ? (
                <>
                  <BootstrapPanel snapshot={consoleSnapshot} />
                  {consoleSnapshot.provenance.channel === 'measured' ? (
                    // The live discovery raster carries the same 141-byte
                    // offer as the QR, so receivers without BarcodeDetector
                    // (every iOS browser) bootstrap through the O1 path.
                    <OpticalField snapshot={consoleSnapshot} />
                  ) : null}
                </>
              ) : consoleSnapshot.phase === 'COMPLETE' ? (
                <CompleteReveal snapshot={consoleSnapshot} mode={mode} />
              ) : (
                <>
                  <OpticalField snapshot={consoleSnapshot} />
                  <SchedulerSeam snapshot={consoleSnapshot} />
                  <CarrierMap snapshot={consoleSnapshot} />
                </>
              )}
            </div>
            <ChannelTelemetry snapshot={consoleSnapshot} />
            <RankField snapshot={consoleSnapshot} />
            <IntegrityChain snapshot={consoleSnapshot} />
          </main>
          <footer className="engineering-toggle">
            <button
              type="button"
              aria-controls="engineering-view"
              aria-expanded={engineeringOpen}
              onClick={() => setEngineeringOpen((value) => !value)}
            >
              <span>{engineeringOpen ? 'CLOSE' : 'OPEN'} ENGINEERING VIEW</span>
              <strong>{consoleSnapshot.opticalProfile} // {consoleSnapshot.acousticProfile}</strong>
            </button>
            {consoleSnapshot.provenance.channel === 'measured' ? (
              <div className="rate-control" aria-label="Physical channel timing">
                <span>REAL TIME / PHYSICAL CHANNEL</span>
                {mode === 'sender' &&
                (consoleSnapshot.phase === 'DISCOVER' || consoleSnapshot.phase === 'KEY_CONFIRM') ? (
                  <button
                    type="button"
                    title="Plays a return-band test burst through this device's own speaker; PROTOCOL-REJECTED ticking up proves the local return listener end to end."
                    onClick={() => void liveSender.runReturnLoopback()}
                  >
                    RETURN LOOPBACK TEST
                  </button>
                ) : null}
                <button
                  type="button"
                  title="Copies the session's bounded diagnostics log as JSON to the clipboard. Nothing leaves this device on its own."
                  onClick={() =>
                    void (mode === 'sender'
                      ? liveSender.copyDiagnostics()
                      : liveReceiver.copyDiagnostics())
                  }
                >
                  COPY DIAGNOSTICS
                </button>
              </div>
            ) : (
              <div
                className="rate-control"
                role="group"
                aria-label="Presentation rate for the simulated model time"
              >
                <span>MODEL TIME</span>
                {PRESENTATION_RATES.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    data-active={presentationRate === rate}
                    aria-pressed={presentationRate === rate}
                    title="Accelerates the simulated channel's model time; throughput figures remain in model time."
                    onClick={() => {
                      presentationRateRef.current = rate;
                      setPresentationRate(rate);
                    }}
                  >
                    ×{rate}
                  </button>
                ))}
              </div>
            )}
            <span>PROTOCOL 1 · NO NETWORK PATH · OBJECT WITHHELD UNTIL VERIFY</span>
          </footer>
          {engineeringOpen ? <div id="engineering-view"><EngineeringView snapshot={consoleSnapshot} /></div> : null}
          {mode === 'receiver' && liveSnapshot !== null && liveReceiver.videoElement !== null ? (
            <aside className="live-camera-dock" aria-label="Live camera aperture">
              <VideoMount element={liveReceiver.videoElement} className="dock-video" />
              <span>LIVE CAMERA / {liveReceiver.cameraFacing === 'user' ? 'FRONT' : 'REAR'}</span>
              <button type="button" onClick={() => void liveReceiver.switchCamera()}>
                SWITCH CAMERA
              </button>
            </aside>
          ) : null}
          {mode === 'receiver' &&
          liveSnapshot !== null &&
          liveSnapshot.phase !== 'COMPLETE' &&
          liveReceiver.returnQrText !== null ? (
            <ReturnOpticalDock text={liveReceiver.returnQrText} />
          ) : null}
          {mode === 'sender' && liveSnapshot !== null && liveSender.returnVideoElement !== null ? (
            <aside className="live-camera-dock" aria-label="Return path camera">
              <VideoMount element={liveSender.returnVideoElement} className="dock-video" />
              <span>RETURN OPTICS / MEASURED</span>
            </aside>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
